/** @jsxImportSource @emotion/react **/
import React, { FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "react-select";

import { LANG_ALL, styleContainer, validURL } from "../../../pages/utils";
import { Context } from "../../../context/EnterprisesContext";
import { BaseInput, Text, TextareaInput } from "../../atoms";
import { ContentSection } from "../../organisms";
import MediaField from "../MediaField";
import * as styles from "../Header/index.styles";

const EnterpriseForm: FC = () => {
  const {
    formData,
    handleChangeFormData: handleChange,
    handleUpdateFormData: handleData,
  } = useContext(Context);

  const { id } = useParams();

  const detail_data = useSelector((state: any) => state.get_enterprises.data);
  const news_data = id ? detail_data?.data.find((r: any) => r.id === id) : null;
  const portofolios = useSelector((state: any) => state.get_portofolio.data);

  const [portofoliosOptions, setPortofoliosOptions] = useState<any>([]);

  type PortofoliosExtract = {
    id: string;
    title: string;
    lang: string;
  };

  useEffect(() => {
    const portofoliosConstructed = portofolios.data?.map(
      ({ title, lang, id }: PortofoliosExtract) => ({
        label: `${title} - ${lang}`,
        value: id,
      })
    );

    setPortofoliosOptions(portofoliosConstructed || []);

    if (!!id) {
      handleData(news_data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, news_data, portofolios]);

  const langInit = news_data?.lang
    ? { value: news_data.lang, label: news_data.lang }
    : { value: "", label: "" };

  const portofolioInit = formData?.portofolio
    ? { value: formData.portofolio, label: formData.portofolio }
    : { value: "", label: "" };

  const [isTitleValid, setTitleValid] = useState<boolean>(false);
  const [isNameValid, setNameValid] = useState<boolean>(false);
  const [isShortDescriptionValid, setShortDescriptionValid] =
    useState<boolean>(false);
  const [isLargeDescriptionValid, setLargeDescriptionValid] =
    useState<boolean>(false);
  const [isFacebookValid, setFacebookValid] = useState<boolean>(false);
  const [isLinkedinValid, setLinkedinValid] = useState<boolean>(false);
  const [isInstagramValid, setInstagramValid] = useState<boolean>(false);
  const [isUrlValid, setUrlValid] = useState<boolean>(false);
  const [isHrefValid, setHrefValid] = useState<boolean>(false);

  useEffect(() => {
    setTitleValid(formData.title.length > 2);
  }, [formData.title]);

  useEffect(() => {
    setNameValid(formData.name.length > 0);
  }, [formData.name]);

  useEffect(() => {
    setShortDescriptionValid(formData.short_description.length > 0);
  }, [formData.short_description]);

  useEffect(() => {
    setLargeDescriptionValid(formData.large_description.length > 0);
  }, [formData.large_description]);

  useEffect(() => {
    const temp = validURL(formData.linkedin);
    setLinkedinValid(temp);
  }, [formData.linkedin]);

  useEffect(() => {
    const temp = validURL(formData.facebook);
    setFacebookValid(temp);
  }, [formData.facebook]);

  useEffect(() => {
    const temp = validURL(formData.instragram);
    setInstagramValid(temp);
  }, [formData.instragram]);

  useEffect(() => {
    const temp = validURL(formData.url);
    setUrlValid(temp);
  }, [formData.url]);

  useEffect(() => {
    setHrefValid(formData.href.length > 0);
  }, [formData.href]);

  const handleLang = (e: any) => {
    handleChange({ lang: e.value });
  };

  return (
    <ContentSection
      style={styleContainer}
      header={
        <React.Fragment>
          <Text textType="h4" weight="black">
            {id ? "Editar" : "Nuevo"} Empresas
          </Text>
        </React.Fragment>
      }
    >
      <BaseInput
        label="Título"
        value={formData.title}
        onChange={(value) => handleChange({ title: value })}
        placeholder="Escribe el título de tu entrada"
        maxLength={500}
        error={!isTitleValid && formData.title.length > 0}
        success={isTitleValid}
        hint={
          !isTitleValid && formData.title.length > 0
            ? "Digite más de dos letras"
            : ""
        }
      />

      <BaseInput
        label="Nombre"
        value={formData.name}
        onChange={(value) => handleChange({ name: value })}
        placeholder="Escribe un nombre"
        maxLength={500}
        error={!isNameValid && formData.name.length > 0}
        success={isNameValid}
        hint={
          !isNameValid && formData.name.length > 0
            ? "Digite más de una letra"
            : ""
        }
      />

      <BaseInput
        label="Descripción corta"
        value={formData.short_description}
        onChange={(value) => handleChange({ short_description: value })}
        placeholder="Escribe una descripción corta"
        maxLength={500}
        error={
          !isShortDescriptionValid && formData.short_description.length > 0
        }
        success={isShortDescriptionValid}
        hint={
          !isShortDescriptionValid && formData.short_description.length > 0
            ? "Digite más de una letra"
            : ""
        }
      />

      <TextareaInput
        label="Descripción larga"
        value={formData.large_description}
        onChange={(value) => handleChange({ large_description: value })}
        placeholder="Escribe la descripción larga"
        maxLength={1500}
        error={
          !isLargeDescriptionValid && formData.large_description.length > 0
        }
        success={isLargeDescriptionValid}
        hint={
          !isLargeDescriptionValid && formData.large_description.length > 0
            ? "Digite más de una letra"
            : ""
        }
      />

      <MediaField
        label="Logo"
        value={formData.logo}
        folder="enterprises/logos"
        onChange={(url) => handleChange({ logo: url })}
      />

      <MediaField
        label="Imagen de tarjeta"
        value={formData.image_card}
        folder="enterprises/cards"
        onChange={(url) => handleChange({ image_card: url })}
      />

      <MediaField
        label="Banner"
        value={formData.banner}
        folder="enterprises/banners"
        onChange={(url) => handleChange({ banner: url })}
      />

      <BaseInput
        label="Linkedin"
        value={formData.linkedin}
        onChange={(value) => handleChange({ linkedin: value })}
        placeholder="URL de Linkedin"
        maxLength={1500}
        error={!isLinkedinValid && formData.linkedin.length > 0}
        success={isLinkedinValid}
        hint={
          !isLinkedinValid && formData.linkedin.length > 0
            ? "Introduce una URL válida"
            : ""
        }
      />

      <BaseInput
        label="Facebook"
        value={formData.facebook}
        onChange={(value) => handleChange({ facebook: value })}
        placeholder="URL de Facebook"
        maxLength={1500}
        error={!isFacebookValid && formData.facebook.length > 0}
        success={isFacebookValid}
        hint={
          !isFacebookValid && formData.facebook.length > 0
            ? "Introduce una URL válida"
            : ""
        }
      />

      <BaseInput
        label="Instagram"
        value={formData.instragram}
        onChange={(value) => handleChange({ instragram: value })}
        placeholder="URL de Instagram"
        maxLength={1500}
        error={!isInstagramValid && formData.instragram.length > 0}
        success={isInstagramValid}
        hint={
          !isInstagramValid && formData.instragram.length > 0
            ? "Introduce una URL válida"
            : ""
        }
      />

      <BaseInput
        label="URL"
        value={formData.url}
        onChange={(value) => handleChange({ url: value })}
        placeholder="URL"
        maxLength={1500}
        error={!isUrlValid && formData.url.length > 0}
        success={isUrlValid}
        hint={
          !isUrlValid && formData.url.length > 0
            ? "Introduce una URL válida"
            : ""
        }
      />

      <BaseInput
        label="Href"
        value={formData.href}
        onChange={(value) => handleChange({ href: value })}
        placeholder="href"
        maxLength={1500}
        error={!isHrefValid && formData.href.length > 0}
        success={isHrefValid}
        hint={
          !isHrefValid && formData.href.length > 0
            ? "Digite más de una letra"
            : ""
        }
      />

      <Text textType="label">
        PORTAFOLIOS:
        <Select
          css={styles.input}
          options={portofoliosOptions}
          defaultValue={portofolioInit}
          onChange={(e: any) => handleChange({ portofolio: e.value })}
        />
      </Text>

      <Text textType="label">
        IDIOMA:{" "}
        <Select
          css={styles.input}
          options={LANG_ALL}
          defaultValue={langInit}
          onChange={(e) => handleLang(e)}
        />
      </Text>
    </ContentSection>
  );
};

export default EnterpriseForm;